/** @module Zoom and pan shared by two panes, so both always show the same part of a card. */

export interface Zoom {
  /** 1 fits the pane; above that the image is enlarged. */
  scale: number;
  /** Middle of the visible part, in fractions of the image. */
  x: number;
  y: number;
}

export const FIT: Zoom = { scale: 1, x: 0.5, y: 0.5 };
export const MIN_SCALE = 1;
export const MAX_SCALE = 8;
/** One wheel notch or one tap on the button. */
export const STEP = 1.4;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** Keep the visible part inside the image; at scale 1 there is nothing to move. */
function settle(zoom: Zoom): Zoom {
  const scale = clamp(zoom.scale, MIN_SCALE, MAX_SCALE);
  const half = 1 / scale / 2;
  return {
    scale,
    x: clamp(zoom.x, half, 1 - half),
    y: clamp(zoom.y, half, 1 - half),
  };
}

/**
 * Zoom towards a point of the pane, so the pixel under the cursor stays put.
 *
 * `at` is where the pointer is, in fractions of the pane: {x: 0.5, y: 0.5} is its middle.
 */
export function zoomAt(zoom: Zoom, factor: number, at: { x: number; y: number }): Zoom {
  const next = clamp(zoom.scale * factor, MIN_SCALE, MAX_SCALE);
  if (next === zoom.scale) return zoom;
  // Where the pointer sits in the image right now.
  const pointer = {
    x: zoom.x + (at.x - 0.5) / zoom.scale,
    y: zoom.y + (at.y - 0.5) / zoom.scale,
  };
  return settle({
    scale: next,
    x: pointer.x - (at.x - 0.5) / next,
    y: pointer.y - (at.y - 0.5) / next,
  });
}

/** Drag by a distance given in fractions of the pane. */
export function panBy(zoom: Zoom, dx: number, dy: number): Zoom {
  return settle({ ...zoom, x: zoom.x - dx / zoom.scale, y: zoom.y - dy / zoom.scale });
}

/** Step the zoom without a pointer: the middle of the pane stays where it is. */
export function stepped(zoom: Zoom, factor: number): Zoom {
  return zoomAt(zoom, factor, { x: 0.5, y: 0.5 });
}

/** The CSS transform that shows this cut-out inside a clipping box. */
export function transformOf(zoom: Zoom): string {
  const shiftX = (0.5 - zoom.x) * 100;
  const shiftY = (0.5 - zoom.y) * 100;
  return `scale(${zoom.scale}) translate(${shiftX}%, ${shiftY}%)`;
}
